import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

export interface MermaidDiagramOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mermaidDiagram: {
      setMermaidDiagram: (attrs?: { code?: string }) => ReturnType;
    };
  }
}

const DEFAULT_MERMAID_CODE = `graph TD
    A[Start] --> B{Decision}
    B -->|Yes| C[Do Something]
    B -->|No| D[Do Something Else]
    C --> E[End]
    D --> E`;

export const MermaidDiagram = Node.create<MermaidDiagramOptions>({
  name: 'mermaidDiagram',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      code: {
        default: DEFAULT_MERMAID_CODE,
      },
    };
  },

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addCommands() {
    return {
      setMermaidDiagram:
        (attrs) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: {
                code: attrs?.code || DEFAULT_MERMAID_CODE,
              },
            })
            .run();
        },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="mermaidDiagram"]',
        getAttrs: (dom) => {
          if (typeof dom === 'string') return {};
          return {
            code: dom.getAttribute('data-code') || DEFAULT_MERMAID_CODE,
          };
        },
      },
      {
        tag: 'span[data-type="mermaidDiagram"]',
        getAttrs: (dom) => {
          if (typeof dom === 'string') return {};
          return {
            code: dom.getAttribute('data-code') || DEFAULT_MERMAID_CODE,
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': 'mermaidDiagram',
        'data-code': node.attrs.code,
        style: 'display: block;',
      }),
      node.attrs.code,
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      // Outer wrapper
      const dom = document.createElement('div');
      dom.classList.add('mermaid-diagram-wrapper');
      dom.setAttribute('data-type', 'mermaidDiagram');
      dom.style.cssText =
        'border: 1px solid hsl(var(--border)); border-radius: 8px; overflow: auto; margin: 8px 0;';

      // Toolbar
      const toolbar = document.createElement('div');
      toolbar.style.cssText =
        'display: flex; align-items: center; justify-content: space-between; padding: 6px 12px; background: hsl(var(--muted)); border-bottom: 1px solid hsl(var(--border)); font-size: 12px; color: hsl(var(--muted-foreground));';

      const label = document.createElement('span');
      label.textContent = '📊 Mermaid Diagram';
      label.style.fontWeight = '600';
      toolbar.appendChild(label);

      const toggleBtn = document.createElement('button');
      toggleBtn.textContent = 'Edit';
      toggleBtn.style.cssText =
        'padding: 2px 10px; border-radius: 4px; border: 1px solid hsl(var(--border)); background: hsl(var(--background)); color: hsl(var(--foreground)); cursor: pointer; font-size: 12px;';
      toolbar.appendChild(toggleBtn);

      dom.appendChild(toolbar);

      // Preview container
      const previewContainer = document.createElement('div');
      previewContainer.style.cssText =
        'padding: 16px; min-height: 100px; background: hsl(var(--background)); width: 100%;';
      previewContainer.classList.add('mermaid-preview');
      dom.appendChild(previewContainer);

      // Edit container
      const editContainer = document.createElement('div');
      editContainer.style.cssText = 'display: none; padding: 8px;';

      const textarea = document.createElement('textarea');
      textarea.value = node.attrs.code || '';
      textarea.style.cssText =
        'width: 100%; min-height: 150px; padding: 12px; border: 1px solid hsl(var(--border)); border-radius: 6px; font-family: monospace; font-size: 13px; resize: vertical; background: hsl(var(--muted)); color: hsl(var(--foreground)); outline: none;';
      textarea.spellcheck = false;

      const applyBtn = document.createElement('button');
      applyBtn.textContent = 'Render Diagram';
      applyBtn.style.cssText =
        'margin-top: 8px; padding: 6px 16px; border-radius: 6px; border: none; background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); cursor: pointer; font-size: 13px; font-weight: 500;';

      editContainer.appendChild(textarea);
      editContainer.appendChild(applyBtn);
      dom.appendChild(editContainer);

      let isEditing = false;
      let renderCounter = 0;

      // Lazy-load mermaid and render
      const renderDiagram = async (code: string) => {
        previewContainer.innerHTML =
          '<span style="color: hsl(var(--muted-foreground)); font-size: 13px;">Rendering diagram…</span>';
        try {
          // @ts-ignore - mermaid types have d3 transitive type conflicts
          const mermaid = (await import('mermaid')).default;
          mermaid.initialize({
            startOnLoad: false,
            theme: 'neutral',
            securityLevel: 'loose',
            fontFamily: 'inherit',
          });
          renderCounter++;
          const id = `mermaid-${Date.now()}-${renderCounter}`;
          const { svg } = await mermaid.render(id, code);
          previewContainer.innerHTML = svg;
          // Ensure the SVG fills the container
          const svgEl = previewContainer.querySelector('svg');
          if (svgEl) {
            svgEl.style.width = '100%';
            svgEl.style.maxWidth = '100%';
            svgEl.style.height = 'auto';
          }
        } catch (err: any) {
          previewContainer.innerHTML = `<div style="color: hsl(var(--destructive)); padding: 12px; font-size: 13px; font-family: monospace; white-space: pre-wrap; background: hsl(var(--destructive) / 0.1); border-radius: 6px;">${
            err.message || 'Invalid mermaid syntax'
          }</div>`;
        }
      };

      // Toggle edit/preview
      const setMode = (editing: boolean) => {
        isEditing = editing;
        if (editing) {
          previewContainer.style.display = 'none';
          editContainer.style.display = 'block';
          toggleBtn.textContent = 'Preview';
          textarea.value = node.attrs.code || '';
          textarea.focus();
        } else {
          previewContainer.style.display = 'flex';
          editContainer.style.display = 'none';
          toggleBtn.textContent = 'Edit';
          renderDiagram(node.attrs.code || '');
        }
      };

      toggleBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!editor.isEditable) return;
        if (isEditing) {
          // Apply changes before switching to preview
          const newCode = textarea.value.trim();
          if (typeof getPos === 'function' && newCode !== node.attrs.code) {
            editor.view.dispatch(
              editor.view.state.tr.setNodeMarkup(getPos(), undefined, {
                ...node.attrs,
                code: newCode,
              }),
            );
          }
        }
        setMode(!isEditing);
      });

      applyBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const newCode = textarea.value.trim();
        if (typeof getPos === 'function') {
          editor.view.dispatch(
            editor.view.state.tr.setNodeMarkup(getPos(), undefined, {
              ...node.attrs,
              code: newCode,
            }),
          );
        }
        setMode(false);
      });

      dom.addEventListener('click', (e) => {
        if (!isEditing && editor.isEditable && typeof getPos === 'function') {
          const pos = getPos();
          editor.commands.setTextSelection(pos);
        }
      });

      // Initial render
      renderDiagram(node.attrs.code || '');

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) return false;
          if (!isEditing) {
            renderDiagram(updatedNode.attrs.code || '');
          }
          return true;
        },
        stopEvent: (event) => {
          // Allow events inside the edit textarea
          if (isEditing && editContainer.contains(event.target as HTMLElement)) {
            return true;
          }
          return false;
        },
        ignoreMutation: () => true,
        destroy: () => {
          // Cleanup
        },
      };
    };
  },

  addProseMirrorPlugins() {
    const nodeType = this.type;
    return [
      new Plugin({
        key: new PluginKey('mermaidPaste'),
        props: {
          handlePaste(view, event) {
            const clipboardText = event.clipboardData?.getData('text/plain')?.trim();
            if (!clipboardText) return false;

            // Detect ```mermaid ... ``` fenced code blocks
            const fencedMatch = clipboardText.match(/^```mermaid\s*\n([\s\S]*?)```\s*$/i);
            // Detect raw mermaid syntax keywords
            const mermaidKeywords =
              /^(sequenceDiagram|graph\s|flowchart\s|classDiagram|stateDiagram|erDiagram|gantt|pie|gitGraph|mindmap|timeline|journey)/i;

            let mermaidCode: string | null = null;

            if (fencedMatch) {
              mermaidCode = fencedMatch[1].trim();
            } else if (mermaidKeywords.test(clipboardText)) {
              mermaidCode = clipboardText;
            }

            if (!mermaidCode) return false;

            const node = nodeType.create({ code: mermaidCode });
            const { tr, selection } = view.state;
            const { $from } = selection;

            // If cursor is inside a block (e.g. paragraph), split it first
            // so the mermaid block node can be inserted at the top level
            if ($from.parent.type.name !== 'doc') {
              const endOfBlock = $from.after();
              tr.insert(endOfBlock, node);
            } else {
              tr.replaceSelectionWith(node);
            }

            view.dispatch(tr);
            return true;
          },
        },
      }),
    ];
  },
});
